// components/workshop/src/components/workshop/skiCapacityLogic.js
//
// Kapasitet/ferdigdato for ski- og skøyteservice. I motsetning til
// sykkel-systemet (capacityCheckLogic.js) er dette ikke et bookingsystem —
// kunden leverer inn utstyr, og ferdigdato regnes ut automatisk ut fra
// hvor mye ski/skøyte-arbeid som allerede ligger i køen samme dag, pluss en
// minimum leveringstid per tjenestetype. Skøyte deler samme kø/budsjett
// (skiDagsbudsjett) og samme overstyrings-flagg (paavirkerSki) som ski,
// siden det antas å være samme benk/tekniker — juster hvis det viser seg
// feil.
//
// Slip av ski er unntaket: den samles ukentlig og følger en fast
// frist-/ferdig-ukedag (skiSlipFristDag/skiSlipFerdigDagFra/-Til på
// ButikkKapasitet) i stedet for kø-basert dagsbudsjett.
import { normalizeWorkshopSlug, isoDate as toISODateLocal } from "./workshopCapacityUtils";

const DAG_NAVN = ["sondag", "mandag", "tirsdag", "onsdag", "torsdag", "fredag", "lordag"];

function normalizeDateOnly(v) {
  if (!v) return "";
  return String(v).split("T")[0];
}

function getDagApiId(date) {
  return DAG_NAVN[date.getDay()];
}

function isAllowedDag(date, allowedDays = []) {
  const norm = (allowedDays || []).map((d) =>
    String(d || "").toLowerCase().replace(/ø/g, "o").replace(/å/g, "a").trim()
  );
  if (!norm.length) return false;
  return norm.includes(getDagApiId(date));
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function getRelevantOverrides(overridesAll, verkstedSlug, overrideFlagField) {
  const wanted = normalizeWorkshopSlug(verkstedSlug);
  return (overridesAll || []).filter((o) => {
    if (normalizeWorkshopSlug(o?.lokasjon || "") !== wanted) return false;
    // undefined/null = ingen eksplisitt scoping, gjelder da alt (bl.a. "stengt"-overstyringer for hele butikken)
    if (overrideFlagField && o?.[overrideFlagField] === false) return false;
    return true;
  });
}

// Effektivt ski/skøyte-dagsbudsjett for en dato, inkl. overstyringer scopet
// til "ikke-sykkel" (paavirkerSki).
function effBudPerDagSki(cap, isoDateStr, verkstedSlug, overridesAll) {
  let dag = cap.skiDagsbudsjett || 0;
  const relevant = getRelevantOverrides(overridesAll, verkstedSlug, "paavirkerSki");
  const dateKey = normalizeDateOnly(isoDateStr);
  const datoOv = relevant.find((x) => normalizeDateOnly(x.dato) === dateKey);
  if (datoOv?.stengt) return 0;
  if (typeof datoOv?.minutter === "number") dag += datoOv.minutter;
  return Math.max(0, dag);
}

// Finner første dag fra og med `fraDato` hvor ski/skøyte-budsjettet har
// plass til `minMinutter`. `fraDato` brukes både som kø-start og som
// implisitt gulv for minimum leveringstid (kall med dropoff + leveringstid
// lagt til på forhånd).
function scanForsteLedigeDag(fraDato, cap, verkstedSlug, overridesAll, perDagMin, minMinutter, maxDager = 90) {
  for (let i = 0; i < maxDager; i++) {
    const date = addDays(fraDato, i);
    if (!isAllowedDag(date, cap.skiApneDager)) continue;

    const iso = toISODateLocal(date);
    const dagMin = effBudPerDagSki(cap, iso, verkstedSlug, overridesAll);
    const brukt = perDagMin[iso] || 0;
    const ledig = Math.max(0, dagMin - brukt);

    if (minMinutter <= 0 || ledig >= minMinutter) return date;
  }
  return null;
}

// Legger til `n` åpne dager (ifølge dagsbudsjettdager) fra en startdato.
function addApneDager(fraDato, n, cap) {
  let d = new Date(fraDato);
  let lagt = 0;
  while (lagt < n) {
    d = addDays(d, 1);
    if (isAllowedDag(d, cap.skiApneDager)) lagt++;
  }
  return d;
}

function nesteUkedag(fraDato, ukedagNavn) {
  const target = DAG_NAVN.indexOf(String(ukedagNavn || "").toLowerCase());
  if (target < 0) return null;
  let d = new Date(fraDato);
  for (let i = 0; i < 14; i++) {
    if (d.getDay() === target) return d;
    d = addDays(d, 1);
  }
  return null;
}

// Slip av ski: samles ukentlig. Hvis levert på/før fristdagen denne uken,
// havner jobben i denne ukens runde; ellers i neste ukes.
function computeSlipBatchFerdigDato(dropoffDato, fristDag, ferdigDagFra, ferdigDagTil) {
  if (!fristDag || !ferdigDagFra) return null;
  const fristDenneUka = nesteUkedag(dropoffDato, fristDag);
  if (!fristDenneUka) return null;

  const frist =
    fristDenneUka >= new Date(new Date(dropoffDato).setHours(0, 0, 0, 0))
      ? fristDenneUka
      : addDays(fristDenneUka, 7);

  const ferdigFra = nesteUkedag(frist, ferdigDagFra);
  const ferdigTil = ferdigDagTil ? nesteUkedag(frist, ferdigDagTil) : ferdigFra;
  return { fra: ferdigFra, til: ferdigTil || ferdigFra, frist };
}

// Hovedfunksjon: regner ut forslag til ferdigdato for en ny ski-/skøytejobb.
//
// - samlesUkentlig: bruker slip-planen på butikken (skiSlipFristDag osv.)
// - ellers: max(dropoff + leveringstidDager åpne dager, første ledige dag
//   i køen basert på skiDagsbudsjett)
export function computeFerdigDatoForskslag({
  cap,
  verkstedSlug,
  overridesAll,
  perDagMin,
  dropoffDato = new Date(),
  totalMin,
  maxLeveringstidDager,
  samlesUkentlig,
}) {
  if (samlesUkentlig && cap.skiSlipFristDag) {
    const batch = computeSlipBatchFerdigDato(dropoffDato, cap.skiSlipFristDag, cap.skiSlipFerdigDagFra, cap.skiSlipFerdigDagTil);
    if (batch) return { dato: batch.til, visning: batch };
  }

  const leveringsDager = Math.max(0, Number(maxLeveringstidDager) || 0);
  const gulvDato = addApneDager(dropoffDato, leveringsDager, cap);
  const dato = scanForsteLedigeDag(gulvDato, cap, verkstedSlug, overridesAll, perDagMin || {}, totalMin || 0);
  return { dato: dato || gulvDato, visning: null };
}

// Utleder totalMin / maxLeveringstidDager / samlesUkentlig for en hel
// service fra dens arbeid-liste (flere arbeid kan være lagt til samtidig).
export function summerArbeidForKapasitet(arbeidListe = []) {
  const totalMin = arbeidListe.reduce((sum, w) => sum + (Number(w?.tidsbruk) || 0), 0);
  const samlesUkentlig = arbeidListe.some((w) => w?.samlesUkentlig);
  const maxLeveringstidDager = arbeidListe.reduce(
    (max, w) => (typeof w?.leveringstidDager === "number" ? Math.max(max, w.leveringstidDager) : max),
    0
  );
  return { totalMin, samlesUkentlig, maxLeveringstidDager };
}
